<template>
  <div class="flex flex-col">
    <div v-if="packageEditions.length">
      <div class="w-full">
        <ul class="flex">
          <li
            v-for="packageEdition in packageEditions"
            :key="packageEdition.id"
            class="flex-1 px-1"
          >
            <PackageEditionRadioButton
              :package-edition="packageEdition"
              :checked="currentPackageEditionId === packageEdition.id"
              :base-price="entity.price"
              @click="setPackageEdition(packageEdition)"
            />
          </li>
        </ul>
      </div>
    </div>

    <div class="mt-16 flex items-baseline">
      <div class="bg-gray-100 p-3 ml-auto rounded">
        <div class="flex -mx-3 items-baseline">
          <div class="flex-1 px-8 py-2">Αρχική Τιμή</div>
          <div class="w-24 px-4 font-medium text-right border-l">{{ entity.price }} &euro;</div>
        </div>
      </div>
    </div>

    <div v-if="hasPackageItems" class="mt-16">
      <h6 class="uppercase text-gray-700 font-medium text mb-2">Βασικα Προϊοντα</h6>
      <p class="text-sm text-teal-700 mb-8">
        Λίστα Προϊόντων που περιλαμβάνονται στη τιμή του πακέτου
      </p>
      <BaseLayoutSpaced>
        <template v-for="packageItem in internalPackageItems">
          <PackageItem
            ref="packageItem"
            v-if="!packageItem.optional"
            :key="packageItem.id"
            :entity="packageItem"
            :selected="isPackageItemSelected(packageItem)"
            @customizing="togglePackageItems"
          >
            <template #default="packageItemProps">
              <BasicProductCustomization
                v-model="packageItem.selectedProductServices"
                :entity="packageItemProps.product"
              />
            </template>
          </PackageItem>
        </template>
      </BaseLayoutSpaced>
    </div>

    <div v-if="hasOptionalPackageItems" class="mt-16">
      <h6 class="uppercase text-gray-700 font-medium text mb-2">Προαιρετικα Προϊοντα</h6>
      <p class="text-sm text-teal-700">Λίστα προαιρετικών Προϊόντων.</p>
      <p class="text-sm text-orange-700 mb-8">
        Η επιλογή τους διαμορφώνει την τελική τιμή του πακέτου.
      </p>
      <BaseLayoutSpaced>
        <template v-for="packageItem in internalPackageItems">
          <PackageItem
            ref="packageItem"
            v-if="packageItem.optional"
            :key="packageItem.id"
            :entity="packageItem"
            :selected="isPackageItemSelected(packageItem)"
            @add="addPackageItem"
            @remove="removePackageItem"
            @customizing="togglePackageItems"
          >
            <template #default="packageItemProps">
              <BasicProductCustomization
                v-model="packageItem.selectedProductServices"
                :entity="packageItemProps.product"
              />
            </template>
          </PackageItem>
        </template>
      </BaseLayoutSpaced>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PackageEditionRadioButton from './packageCustomization/PackageEditionRadioButton.vue';
import PackageItem from './packageCustomization/PackageItem.vue';
import BasicProductCustomization from './BasicProductCustomization.vue';

export default {
  name: 'PackageCustomization',
  components: {
    PackageEditionRadioButton,
    PackageItem,
    BasicProductCustomization,
  },
  props: {
    entity: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    internalPackageItems: [],
    selectedPackageItems: [],
    currentPackageEditionId: null,
  }),
  computed: {
    ...mapState('entities', {
      productsById: (state) => state.products.byId,
      packageItemsById: (state) => state.packageItems.byId,
      packageEditionsById: (state) => state.packageEditions.byId,
    }),
    packageEditions() {
      return this.entity.packageEditions.map((id) => this.packageEditionsById[id]);
    },
    currentPackageEdition() {
      if (!this.currentPackageEditionId) return null;

      return this.packageEditionsById[this.currentPackageEditionId] || null;
    },
    packageItems() {
      return this.entity.packageItems
        .map((id) => this.packageItemsById[id])
        .filter((packageItem) => this.productsById[packageItem.productId]);
    },
    hasPackageItems() {
      return this.packageItems.some((packageItem) => !packageItem.optional);
    },
    hasOptionalPackageItems() {
      return this.packageItems.some((packageItem) => packageItem.optional);
    },
  },
  watch: {
    selectedPackageItems: {
      handler(newSelectedPackageItems) {
        this.$emit('changed', newSelectedPackageItems);
      },
      deep: true,
    },
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.internalPackageItems = [...this.packageItems.map(this.getPackageItemWithDefaults)];

      this.setDefaultPackageItems();
    },
    setDefaultPackageItems() {
      this.selectedPackageItems = [
        ...this.internalPackageItems.filter((packageItem) => !packageItem.optional),
      ];
    },
    getPackageItemWithDefaults(packageItem) {
      return {
        ...packageItem,
        selectedProductServices: [],
      };
    },
    isPackageItemSelected(packageItem) {
      const index = this.selectedPackageItems.findIndex((x) => x.id === packageItem.id);

      return index >= 0;
    },
    togglePackageItems(sourcePackageItemId) {
      const components = Array.isArray(this.$refs.packageItem)
        ? this.$refs.packageItem
        : [this.$refs.packageItem];

      components.forEach((component) => {
        if (component.entity.id === sourcePackageItemId || !component.customize) {
          return;
        }
        component.toggleCustomizeState();
      });
    },
    setPackageEdition(packageEdition) {
      this.currentPackageEditionId = packageEdition.id;

      const { packageItems = [] } = this.currentPackageEdition || {};

      this.setup();

      packageItems.forEach((id) => {
        const packageItem = this.internalPackageItems.find((x) => x.id === id);

        if (!packageItem) return;

        this.selectedPackageItems.push(packageItem);
      });
    },
    addPackageItem(packageItem) {
      this.selectedPackageItems.push(packageItem);
      this.updateCurrentPackageEdition();
    },
    updateCurrentPackageEdition() {
      const matchedPackageEdition = this.packageEditions
        .slice(0)
        .reverse()
        .find((packageEdition) =>
          packageEdition.packageItems.every(
            (id) => this.selectedPackageItems.findIndex((x) => x.id === id) >= 0
          )
        );

      if (matchedPackageEdition) {
        this.currentPackageEditionId = matchedPackageEdition.id;
      }
    },
    removePackageItem(packageItem) {
      const index = this.selectedPackageItems.findIndex((x) => x.id === packageItem.id);

      if (index >= 0) {
        this.selectedPackageItems.splice(index, 1);
      }

      this.resetPackageItem(packageItem);

      this.updateCurrentPackageEdition();
    },
    resetPackageItem(packageItem) {
      const index = this.internalPackageItems.findIndex((x) => x.id === packageItem.id);

      if (index >= 0) {
        this.internalPackageItems.splice(index, 1, this.getPackageItemWithDefaults(packageItem));
      }
    },
  },
};
</script>
